import { useEffect, useState } from 'react';
import { getDatabase, ref, onValue, set } from "firebase/database";
import { initializeApp } from "firebase/app";

// Initialize once (config from env vars)
// Using REACT_APP_ prefix for Create React App environment
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Check if config is valid to avoid immediate crash on load if env vars are missing
const isConfigValid = Object.values(firebaseConfig).some(value => !!value);
if (!isConfigValid) {
  console.warn("Firebase configuration is missing. useArtSync will not function correctly.");
}

const app = initializeApp(firebaseConfig);
const db = getDatabase(app);

export function useArtSync() {
 const [currentUri, setCurrentUri] = useState(null);
 const [settings, setSettingsState] = useState({
   animationDuration: 600 // default 10 minutes
 });

 // Listener for artwork (For Display)
 useEffect(() => {
   const artRef = ref(db, 'session/current_artwork');
   const unsubscribe = onValue(artRef, (snapshot) => {
     const data = snapshot.val();
     if (data && data.uri) {
       // Only update if URI actually changed
       setCurrentUri(prev => prev === data.uri ? prev : data.uri);
     }
   }, (error) => {
       console.error("Firebase Read Error:", error);
   });
   return () => unsubscribe();
 }, []);

 // Listener for settings (For Display)
 useEffect(() => {
   const settingsRef = ref(db, 'session/settings');
   const unsubscribe = onValue(settingsRef, (snapshot) => {
     const data = snapshot.val();
     if (data) {
       // Only update if values actually changed
       setSettingsState(prev => {
         const newState = { ...prev, ...data };
         if (JSON.stringify(prev) === JSON.stringify(newState)) return prev;
         return newState;
       });
     }
   }, (error) => {
       console.error("Firebase Settings Read Error:", error);
   });
   return () => unsubscribe();
 }, []);

 // Setter for artwork (For Remote)
 const setArtwork = (uri) => {
   set(ref(db, 'session/current_artwork'), {
     uri: uri,
     timestamp: Date.now()
   }).catch(err => console.error("Firebase Write Error:", err));
 };

 // Setter for settings (For Remote)
 // Only write to Firebase - let the listener update local state to avoid double updates
 const setSettings = (newSettings) => {
   const updated = { ...settings, ...newSettings };
   set(ref(db, 'session/settings'), updated)
     .catch(err => console.error("Firebase Settings Write Error:", err));
 };

 return { currentUri, setArtwork, settings, setSettings };
}
